import React, { useEffect, useState } from "react";
import { biGet, today, errText } from "./bi/biApi";
import { Shell, Card, Kpi, Tabs, Empty, Loading, ErrorBox, selectCls } from "./bi/BiUi";
import { COLORS, AttendancePie, MultiLine, HBars } from "./bi/DashCharts";
import { ComplianceGrid, ComplianceHeatmap, ComplianceLongitudinal } from "./bi/StaffAudit";
import StaffComplianceReport from "./bi/StaffComplianceReport";
import { StudentTable } from "./bi/ExecPanels";
import { PairScatter } from "./bi/DashExtras";
import Student360Modal from "./bi/Student360Modal";
import ActiveClassesModal from "./bi/ActiveClassesModal";
import * as ex from "./bi/auditExports";

const SETTINGS_ROLES = ["principal", "admin", "admin1"];

const CLASS_HIERARCHY = {
  "Nursery": 1, "LKG": 2, "HKG": 3,
  "1st": 10, "2nd": 20, "3rd": 30, "4th": 40, "5th": 50,
  "6th": 60, "7th": 70, "8th": 80, "9th": 90, "10th": 100,
  "11th": 110, "12th": 120
};

const getRank = (name) => {
  if (!name) return 999;
  const key = Object.keys(CLASS_HIERARCHY).find(h => name.startsWith(h));
  return key ? CLASS_HIERARCHY[key] : 500;
};

const sortClasses = (a, b) => {
  const rankA = getRank(a);
  const rankB = getRank(b);
  if (rankA !== rankB) return rankA - rankB;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

/** Principal / Super Admin command hub: KPI row + multi-chart grid. Scope: whole school or one class. */
export default function ExecutiveSchoolHub({ user }) {
  const [scope, setScope] = useState("school");            // "school" | "class"
  const [activeOnly, setActiveOnly] = useState(true);      // Active Classes Only Toggle
  const [classes, setClasses] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [classId, setClassId] = useState("");
  const [range, setRange] = useState("weekly");
  const [daily, setDaily] = useState(null);
  const [trend, setTrend] = useState(null);
  const [comp, setComp] = useState(null);
  const [audit, setAudit] = useState(null);                // 7-day audit
  const [longAudit, setLongAudit] = useState(null);        // 12-month audit
  const [yearly, setYearly] = useState(null);
  const [pick, setPick] = useState(null);                  // { classId, studentId }
  const [err, setErr] = useState("");
  const [showCfg, setShowCfg] = useState(false);
  const [reload, setReload] = useState(0);
  const date = today();
  const cid = scope === "class" ? classId : "";
  const fail = (e) => setErr(errText(e));
  const params = cid ? { classId: cid, date, activeOnly } : { date, activeOnly };

  useEffect(() => {
    biGet(user, "/api/classes").then((r) => {
      // Filter out inactive if activeOnly is set.
      const raw = r || [];
      const filtered = activeOnly ? raw.filter(c => c.isActive !== false) : raw;
      const names = [...new Set(filtered.map((c) => c.name).filter(Boolean))].sort(sortClasses);
      setClasses(names);
      setActiveCount(new Set(raw.filter((c) => c.isActive !== false).map((c) => c.name)).size);
      setClassId((cur) => (names.includes(cur) ? cur : names[0] || ""));
    }).catch(fail);
    biGet(user, "/api/analytics/compliance", { date, activeOnly }).then(setComp).catch(fail);
    biGet(user, "/api/analytics/staff-audit", { timeframe: "7days", activeOnly }).then(setAudit).catch(fail);
    biGet(user, "/api/analytics/staff-audit", { timeframe: "12months", activeOnly }).then(setLongAudit).catch(fail);
  }, [user, date, activeOnly, reload]);

  useEffect(() => {
    if (scope === "class" && !classId) return;
    setErr(""); setDaily(null); setYearly(null);
    biGet(user, "/api/analytics/daily", params).then(setDaily).catch(fail);
    biGet(user, "/api/analytics/yearly", params).then(setYearly).catch(fail);
  }, [user, cid, scope, date, activeOnly, reload]);

  useEffect(() => {
    if (scope === "class" && !classId) return;
    setTrend(null);
    biGet(user, `/api/analytics/${range}`, params).then(setTrend).catch(fail);
  }, [user, cid, scope, range, date, activeOnly, reload]);

  const series = trend ? (trend.days || trend.trend || []) : [];
  const bars = yearly
    ? yearly.perClass.map((c) => ({ label: `Class ${c.classId}`, classId: c.classId, attendance: c.avgAttendance, homework: c.avgHomework }))
      .sort((a, b) => sortClasses(a.classId, b.classId))
    : [];
  const totalStudents = yearly ? yearly.perClass.reduce((s, c) => s + c.students, 0) : null;
  const hasToday = !!(daily && (daily.markedTotal ?? daily.total) > 0);
  const [attView, setAttView] = useState("daily");
  const ctx = { date, comp, daily, audit, view: attView };
  const btn = "shrink-0 min-h-[40px] rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] sm:min-h-0 font-black text-slate-700 shadow-sm hover:border-slate-300";


  return (
    <Shell>
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:pb-6 md:flex-row md:flex-wrap md:items-center md:gap-4">
        <div className="min-w-0 md:mr-auto">
          <h2 className="truncate text-xl font-black tracking-tight text-slate-900 sm:text-2xl lg:text-3xl">Executive School Hub</h2>
          <p className="text-xs font-semibold text-slate-500 sm:text-sm">
            {scope === "school" ? "Whole School Intelligence" : `Class ${classId} Drilldown`} {"\u2022"} <span className="text-sky-600 font-bold">{date}</span>
          </p>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Active Classes Toggle */}
          <button onClick={() => setActiveOnly(!activeOnly)}
            className={`flex min-h-[44px] min-w-0 flex-1 items-center justify-center gap-2 rounded-full border px-3 py-2 text-xs font-black transition-all sm:min-h-0 sm:flex-none sm:px-4 ${activeOnly ? "bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            <span className="truncate">{activeOnly ? "\u2713 Active Classes" : "All Classes"}</span>
            <span className={`flex h-5 shrink-0 items-center rounded-full px-2 text-[10px] ${activeOnly ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>
              {activeOnly ? `${activeCount} Active` : `${classes.length} Total`}
            </span>
          </button>

          {SETTINGS_ROLES.includes(user?.role) && (
            <button onClick={() => setShowCfg(true)} aria-label="Configure School Active Classes" title="Configure School Active Classes"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-xs font-black text-slate-700 shadow-sm hover:border-slate-300 sm:h-auto sm:w-auto sm:px-4 sm:py-2">
              {"\u2699\uFE0F"}<span className="ml-1 hidden sm:inline lg:hidden">Configure</span><span className="ml-1 hidden lg:inline">Configure School Active Classes</span>
            </button>
          )}
        </div>

        <div className="max-w-full">
          <Tabs value={scope} onChange={setScope} options={[["school", "Whole School"], ["class", "Class View"]]} />
        </div>
      </div>
      
      {scope === "class" && (
        <div className="no-scrollbar -mx-3 flex gap-2 overflow-x-auto whitespace-nowrap px-3 py-2 sm:mx-0 sm:px-0">
          {classes.map((c) => (
            <button key={c} onClick={() => setClassId(c)}
              className={`shrink-0 min-h-[40px] px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border ${classId === c ? "bg-slate-900 text-white border-slate-900 shadow-md" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              {c}
            </button>
          ))}
        </div>
      )}
      
      <div className="no-scrollbar -mx-3 flex items-center gap-2 overflow-x-auto whitespace-nowrap px-3 py-2 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:whitespace-normal sm:px-0">
        <button className={btn} onClick={() => ex.saveAnalyticsSnapshot(document.getElementById("analytics-grid-container"), { date, format: "png" }).catch(fail)}>📷 <span className="sm:hidden">PNG</span><span className="hidden sm:inline">Save Analytics Snapshot (PNG)</span></button>
        <button className={btn} onClick={() => ex.saveAnalyticsSnapshot(document.getElementById("analytics-grid-container"), { date, format: "pdf" }).catch(fail)}>📷 <span className="sm:hidden">PDF</span><span className="hidden sm:inline">Snapshot (PDF)</span></button>
        <span className="mx-1 hidden h-5 w-px bg-slate-200 sm:block" />
        <select className={btn} value={attView} onChange={(e) => setAttView(e.target.value)}>
          <option value="daily">Attendance: Daily</option>
          <option value="7day">Attendance: 7-Day</option>
        </select>
        <button className={btn} disabled={!comp} onClick={() => ex.exportAttendancePdf(ctx)}>📄 <span className="sm:hidden">Attendance</span><span className="hidden sm:inline">Export Attendance Audit (PDF)</span></button>
        <button className={btn} disabled={!comp} onClick={() => ex.shareOnWhatsapp(ex.attendanceWhatsappText(ctx))}>📲 <span className="sm:hidden">Attendance</span><span className="hidden sm:inline">Share Attendance on WhatsApp</span></button>
        <button className={btn} disabled={!comp} onClick={() => ex.exportDiaryPdf(ctx)}>📄 <span className="sm:hidden">Diary</span><span className="hidden sm:inline">Export Diary Audit (PDF)</span></button>
        <button className={btn} disabled={!comp} onClick={() => ex.shareOnWhatsapp(ex.diaryWhatsappText(ctx))}>📲 <span className="sm:hidden">Diary</span><span className="hidden sm:inline">Share Diary on WhatsApp</span></button>
      </div>

      <ErrorBox message={err} />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi label="Total students" value={totalStudents == null ? "..." : totalStudents} tone="indigo" icon={"\u{1F393}"} hint="Active enrollment" />
        <Kpi label="Attendance rate" value={daily ? (hasToday ? `${daily.attendancePct}%` : "Pending") : "..."} tone="emerald" icon={"\u{1F3EB}"} hint={daily ? `${daily.present}/${daily.total} present` : "Not marked yet"} />
        <Kpi label="Staff compliance" value={comp ? `${comp.compliancePct}%` : "..."} tone="cyan" icon={"\u2705"} hint={comp ? `${comp.completed}/${comp.total} classes synced` : ""} />
        <Kpi label="Approved leaves" value={daily ? daily.leaveCount : "..."} tone="amber" icon={"\u{1F7E1}"} hint="Today" />
      </div>

      <div id="analytics-grid-container" className="grid grid-cols-1 gap-4 bg-white sm:gap-6 lg:grid-cols-2">
        <Card title="Attendance Distribution" subtitle="Daily presence vs absence vs leave breakdown">
          {!daily ? <Loading /> : hasToday
            ? <AttendancePie present={daily.present} absent={daily.absent} leave={daily.leaveCount} centerLabel={`${daily.attendancePct}%`} />
            : <Empty>Attendance data is not available for today yet.</Empty>}
        </Card>

        <Card title="Attendance & Homework Trends" 
          right={<Tabs value={range} onChange={setRange} options={[["weekly", "7D"], ["monthly", "30D"]]} />}>
          {!trend ? <Loading /> : series.length
            ? <MultiLine data={series} refLine={75} series={[
              { key: "attendancePct", name: "Attendance", color: COLORS.present },
              { key: "homeworkPct", name: "Homework", color: COLORS.academic }]} />
            : <Empty />}
        </Card>

        {scope === "school" && (
          <Card title="Inter-Class Performance" subtitle="Average attendance and homework discipline comparison">
            {!yearly ? <Loading /> : bars.length
              ? <HBars data={bars} bars={[
                { key: "attendance", name: "Attendance", color: COLORS.present },
                { key: "homework", name: "Homework", color: COLORS.academic }]} />
              : <Empty>No class performance data found.</Empty>}
          </Card>
        )}

        <Card title="Today's Live Submission Tracker" subtitle={comp ? `${comp.completed}/${comp.total} classes completed today` : ""}
          right={
            <div className="no-scrollbar flex max-w-full gap-2 overflow-x-auto whitespace-nowrap sm:flex-wrap sm:overflow-visible sm:whitespace-normal">
              <button className={btn} disabled={!comp} onClick={() => ex.exportAttendanceOnlyPdf(ctx)}>{"\u{1F4C4}"} Download Attendance Report (PDF)</button>
              <button className={btn} disabled={!comp} onClick={() => ex.shareOnWhatsapp(ex.attendanceOnlyWhatsappText(ctx))}>{"\u{1F4F2}"} WhatsApp Attendance Report</button>
              <button className={btn} disabled={!comp} onClick={() => ex.exportTimelinessPdf(ctx)}>{"\u{1F4C4}"} Download Timeliness Log (PDF)</button>
            </div>
          }>
          {!comp ? <Loading /> : comp.rows.length ? <ComplianceGrid rows={comp.rows} /> : <Empty />}
        </Card>

        <StaffComplianceReport user={user} activeOnly={activeOnly} classId={cid} reload={reload} />

        <Card title="7-Day Staff Compliance Audit" subtitle="Timeliness & Submission Consistency Heatmap"
          right={<button className={btn} disabled={!comp || !audit} onClick={() => ex.exportTimelinessPdf(ctx)}>📄 Download Timeliness Log (PDF)</button>}>
           {!audit ? <Loading /> : <ComplianceHeatmap matrix={audit.matrix} />}
        </Card>

        <Card title="12-Month Longitudinal Record" subtitle="Month-over-month submission consistency overview">
           {!longAudit ? <Loading /> : <ComplianceLongitudinal report={longAudit.report} />}
        </Card>

        <Card title="Academic Impact Analysis"
          subtitle={yearly && yearly.correlation.coefficient != null ? `Correlation: ${yearly.correlation.coefficient} (Sample: ${yearly.correlation.sample})` : "Attendance % vs Exam %"}>
          {!yearly ? <Loading /> : yearly.correlation.points.length
            ? <PairScatter points={yearly.correlation.points} />
            : <Empty>Insufficient data for impact analysis.</Empty>}
        </Card>
      </div>

      {yearly && <StudentTable students={yearly.students} onPick={setPick} />}
      {showCfg && <ActiveClassesModal user={user} onClose={() => setShowCfg(false)} onSaved={() => setReload((n) => n + 1)} />}
      {pick && yearly && <Student360Modal user={user} classId={pick.classId} studentId={pick.studentId} year={yearly.academicYear} onClose={() => setPick(null)} />}
    </Shell>
  );
}


